import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Save, Users, ShoppingBag, Settings as SettingsIcon, ArrowLeft, Activity, RefreshCw, ExternalLink, Package, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MotoDirectAdmin() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('live');
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [markup, setMarkup] = useState(500);
  const [multiplier, setMultiplier] = useState(1.20);
  const [savingMarkup, setSavingMarkup] = useState(false);
  const [savingMultiplier, setSavingMultiplier] = useState(false);

  // External moto-direct.nl overview state
  const [live, setLive] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState(null);
  const [liveFetchedAt, setLiveFetchedAt] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const [oRes, cRes, sRes] = await Promise.all([
        axios.get(`${API}/motodirect/admin/orders`, auth),
        axios.get(`${API}/motodirect/admin/customers`, auth),
        axios.get(`${API}/motodirect/admin/settings`, auth),
      ]);
      setOrders(oRes.data.orders || []);
      setCustomers(cRes.data.customers || []);
      setMarkup(sRes.data.markup ?? 500);
      setMultiplier(sRes.data.dealer_multiplier ?? 1.20);
    } catch (e) {
      toast.error('Kon MotoDirect data niet laden');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const loadLive = async () => {
    setLiveLoading(true);
    setLiveError(null);
    try {
      const res = await axios.get(`${API}/motodirect/admin/external-overview`, auth);
      setLive(res.data);
      setLiveFetchedAt(new Date());
    } catch (e) {
      setLiveError(e?.response?.data?.detail || 'Kon live gegevens niet ophalen');
    } finally {
      setLiveLoading(false);
    }
  };
  useEffect(() => { loadLive(); }, []);

  const saveMarkup = async () => {
    setSavingMarkup(true);
    try {
      const res = await axios.put(`${API}/motodirect/admin/settings`, { markup: Number(markup) }, auth);
      setMarkup(res.data.markup);
      toast.success(`Marge bijgewerkt naar €${res.data.markup}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSavingMarkup(false);
    }
  };

  const saveMultiplier = async () => {
    setSavingMultiplier(true);
    try {
      const res = await axios.put(`${API}/motodirect/admin/settings`, { dealer_multiplier: Number(multiplier) }, auth);
      setMultiplier(res.data.dealer_multiplier);
      toast.success(`Dealervergelijking bijgewerkt (×${res.data.dealer_multiplier})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSavingMultiplier(false);
    }
  };

  const formatPrice = (v) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);
  const formatDate = (iso) => iso ? new Date(iso).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : '—';

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/admin/dashboard" className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1 mb-2" data-testid="admin-back">
              <ArrowLeft className="w-3 h-3" /> Admin dashboard
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">MotoDirect Beheer</h1>
            <p className="text-gray-600 text-sm">Bestellingen, klanten en marge-instellingen voor moto-direct.nl</p>
          </div>
          <div className="flex gap-3">
            <StatCard label="Bestellingen" value={orders.length} icon={ShoppingBag} />
            <StatCard label="Klanten" value={customers.length} icon={Users} />
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6 flex gap-1">
          {[
            { id: 'live', label: 'MotoDirect.nl Live', icon: Activity },
            { id: 'orders', label: 'Bestellingen', icon: ShoppingBag },
            { id: 'customers', label: 'Klanten', icon: Users },
            { id: 'settings', label: 'Instellingen', icon: SettingsIcon },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${tab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : tab === 'live' ? (
          <LivePanel
            live={live}
            loading={liveLoading}
            error={liveError}
            fetchedAt={liveFetchedAt}
            onRefresh={loadLive}
            formatDate={formatDate}
          />
        ) : tab === 'orders' ? (
          <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="orders-table">
            {orders.length === 0 ? (
              <div className="py-20 text-center text-gray-400 text-sm">Nog geen bestellingen.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3">Datum</th>
                    <th className="text-left px-4 py-3">Klant</th>
                    <th className="text-left px-4 py-3">Motor</th>
                    <th className="text-left px-4 py-3">Keuring</th>
                    <th className="text-left px-4 py-3">Taxatie</th>
                    <th className="text-right px-4 py-3">Aanbetaling</th>
                    <th className="text-right px-4 py-3">Totaal</th>
                    <th className="text-left px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orders.map(o => (
                    <tr key={o.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(o.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{o.buyer_name}</div>
                        <div className="text-xs text-gray-500">{o.buyer_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{o.motorcycle_snapshot?.brand} {o.motorcycle_snapshot?.model}</div>
                        <div className="text-xs text-gray-500">{o.motorcycle_snapshot?.year}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {o.keuring_choice === 'self' ? 'Klant zelf' : (o.keuring_choice === 'motodirect' ? 'Moto-direct' : (o.inspection_choice === 'motodirect' ? 'Moto-direct' : 'MotoImport'))}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {o.include_taxatie ? <span className="text-green-600 font-medium">Ja (+€160)</span> : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">{formatPrice(o.deposit_amount)}</td>
                      <td className="px-4 py-3 text-right font-semibold">{formatPrice(o.total_price)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-1 rounded ${o.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                          {o.payment_status === 'paid' ? 'Betaald' : 'In afwachting'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : tab === 'customers' ? (
          <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="customers-table">
            {customers.length === 0 ? (
              <div className="py-20 text-center text-gray-400 text-sm">Nog geen klanten.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3">Registratie</th>
                    <th className="text-left px-4 py-3">Naam</th>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Telefoon</th>
                    <th className="text-left px-4 py-3">Woonplaats</th>
                    <th className="text-left px-4 py-3">BSN</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {customers.map(c => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(c.created_at)}</td>
                      <td className="px-4 py-3 font-medium">{c.contact_person || c.company_name}</td>
                      <td className="px-4 py-3 text-gray-600">{c.email}</td>
                      <td className="px-4 py-3 text-gray-600">{c.phone || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{c.postal_code} {c.city}</td>
                      <td className="px-4 py-3 text-gray-500 font-mono">{c.bsn || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <div className="space-y-6 max-w-2xl" data-testid="settings-panel">
            <div className="bg-white border border-gray-200 p-8">
              <h2 className="text-lg font-bold text-gray-900 mb-2">Marge (mark-up) op dealerprijs</h2>
              <p className="text-sm text-gray-600 mb-6">
                Dit bedrag wordt automatisch bovenop de dealerprijs opgeteld voor <b>alle</b> motoren op moto-direct.nl. Klanten zien alleen de eindprijs (verstopte marge). Wijzigingen zijn direct actief.
              </p>
              <div className="flex items-center gap-3">
                <div className="flex items-center border border-gray-300 focus-within:border-blue-600 bg-white">
                  <span className="px-3 text-gray-500 border-r border-gray-300">€</span>
                  <input
                    type="number"
                    min="0"
                    step="10"
                    value={markup}
                    onChange={(e) => setMarkup(e.target.value)}
                    data-testid="markup-input"
                    className="px-3 py-3 outline-none w-40 text-right font-semibold"
                  />
                </div>
                <button
                  onClick={saveMarkup}
                  disabled={savingMarkup}
                  data-testid="save-markup-btn"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-3 flex items-center gap-2 disabled:opacity-70"
                >
                  {savingMarkup ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Opslaan
                </button>
              </div>
              <div className="mt-6 text-xs text-gray-500 border-t border-gray-100 pt-4">
                Voorbeeld: dealerprijs €10.000 + marge €{markup || 0} = <b>€{new Intl.NumberFormat('nl-NL').format(10000 + Number(markup || 0))}</b> zichtbaar voor particulier
              </div>
            </div>

            <div className="bg-white border border-gray-200 p-8">
              <h2 className="text-lg font-bold text-gray-900 mb-2">Vergelijkbare dealerprijs — bespaar-effect</h2>
              <p className="text-sm text-gray-600 mb-6">
                Deze factor bepaalt welke {'"'}bij dealer{'"'} prijs klanten zien (doorgestreept) om het besparingseffect te tonen. Standaard 1.20 = 20% hoger dan onze prijs. Verhoog voor sterkere psychologische aantrekkingskracht.
              </p>
              <div className="flex items-center gap-3">
                <div className="flex items-center border border-gray-300 focus-within:border-blue-600 bg-white">
                  <span className="px-3 text-gray-500 border-r border-gray-300">×</span>
                  <input
                    type="number"
                    min="1.00"
                    max="3.00"
                    step="0.05"
                    value={multiplier}
                    onChange={(e) => setMultiplier(e.target.value)}
                    data-testid="multiplier-input"
                    className="px-3 py-3 outline-none w-32 text-right font-semibold"
                  />
                </div>
                <button
                  onClick={saveMultiplier}
                  disabled={savingMultiplier}
                  data-testid="save-multiplier-btn"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-3 flex items-center gap-2 disabled:opacity-70"
                >
                  {savingMultiplier ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Opslaan
                </button>
              </div>
              <div className="mt-6 text-xs text-gray-500 border-t border-gray-100 pt-4">
                Voorbeeld: bij onze prijs van €12.500, ziet klant {'"Bij dealer '}<b>€{new Intl.NumberFormat('nl-NL').format(Math.ceil(12500 * Number(multiplier || 1.2) / 100) * 100)}</b>{'"'} doorgestreept — <b className="text-green-600">Jij bespaart €{new Intl.NumberFormat('nl-NL').format((Math.ceil(12500 * Number(multiplier || 1.2) / 100) * 100) - 12500)}</b>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-gray-200 px-5 py-3 flex items-center gap-3">
      <Icon className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
      <div>
        <div className="text-xs uppercase tracking-widest text-gray-500">{label}</div>
        <div className="text-xl font-bold text-gray-900">{value}</div>
      </div>
    </div>
  );
}

function LiveKpi({ label, value, sub, icon: Icon, highlight }) {
  return (
    <div className={`border p-5 ${highlight ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-widest text-gray-500">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-gray-400" strokeWidth={1.5} />}
      </div>
      <div className="text-3xl font-bold text-gray-900">{value ?? 0}</div>
      {sub != null && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function LivePanel({ live, loading, error, fetchedAt, onRefresh, formatDate }) {
  return (
    <div className="space-y-6" data-testid="live-panel">
      <div className="bg-white border border-gray-200 p-5 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-blue-600" />
            <h2 className="text-base font-bold text-gray-900">Live vanaf moto-direct.nl</h2>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Realtime overzicht van de B2C consumentenportal.{' '}
            {fetchedAt && <span>Laatst opgehaald: {fetchedAt.toLocaleTimeString('nl-NL')}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="https://moto-direct.nl"
            target="_blank"
            rel="noreferrer"
            data-testid="open-motodirect-btn"
            className="text-xs text-gray-600 hover:text-gray-900 inline-flex items-center gap-1 border border-gray-300 px-3 py-2"
          >
            Open moto-direct.nl <ExternalLink className="w-3 h-3" />
          </a>
          <button
            onClick={onRefresh}
            disabled={loading}
            data-testid="refresh-live-btn"
            className="text-xs text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-70 inline-flex items-center gap-2 px-3 py-2"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Vernieuwen
          </button>
        </div>
      </div>

      {error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 text-sm" data-testid="live-error">
          <b>Fout:</b> {error}
        </div>
      ) : loading && !live ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
        </div>
      ) : live ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="live-kpis">
            <LiveKpi label="Bestellingen" value={live?.counts?.orders} sub={`${live?.counts?.orders_open ?? 0} open`} icon={ShoppingBag} highlight />
            <LiveKpi label="Klanten" value={live?.counts?.customers} icon={Users} />
            <LiveKpi label="Lease-aanvragen" value={live?.counts?.leases} sub={`${live?.counts?.leases_open ?? 0} open`} icon={FileText} />
            <LiveKpi label="Motor-verzoeken" value={live?.counts?.motor_requests} sub={`${live?.counts?.motor_requests_open ?? 0} open`} icon={Package} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <LiveListCard title="Recente bestellingen" items={live?.recent_orders} type="order" formatDate={formatDate} />
            <LiveListCard title="Recente klanten" items={live?.recent_customers} type="customer" formatDate={formatDate} />
            <LiveListCard title="Recente lease-aanvragen" items={live?.recent_leases} type="lease" formatDate={formatDate} />
            <LiveListCard title="Recente motor-verzoeken" items={live?.recent_motor_requests} type="request" formatDate={formatDate} />
          </div>
        </>
      ) : null}
    </div>
  );
}

function LiveListCard({ title, items, type, formatDate }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <div className="bg-white border border-gray-200" data-testid={`live-list-${type}`}>
      <div className="px-5 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      </div>
      {list.length === 0 ? (
        <div className="px-5 py-10 text-center text-xs text-gray-400">Nog niets.</div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {list.slice(0, 5).map((it, idx) => (
            <li key={it.id || idx} className="px-5 py-3 text-sm">
              {type === 'customer' ? (
                <div>
                  <div className="font-medium text-gray-900">{it.email}</div>
                  <div className="text-xs text-gray-500">
                    {[it.phone, it.city, it.role].filter(Boolean).join(' • ')}
                    {it.created_at && <span> • {formatDate(it.created_at)}</span>}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="font-medium text-gray-900">
                    {it.title || it.motorcycle_title || it.brand || it.email || it.buyer_email || `#${(it.id || '').slice(0, 8)}`}
                  </div>
                  <div className="text-xs text-gray-500 space-x-2">
                    {it.status && <span className="inline-block px-2 py-0.5 bg-gray-100 rounded">{it.status}</span>}
                    {it.total_price != null && <span>€{it.total_price}</span>}
                    {it.created_at && <span>{formatDate(it.created_at)}</span>}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
